import React, { forwardRef, useCallback, useRef } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import { DownloadIcon } from '../../assets/svg/SVGComponents';

const Downloads = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border: 1px solid #d0d0d0;
    background: #fff;
    border-radius: 0.5rem;
    padding: 0.3rem 0.7rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 0.8rem;
    cursor: pointer;

    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

/** Link público do telão a partir do token do torneio. */
export const boardUrl = (token: string): string =>
  `${window.location.origin}/telao/${token}`;

function download(href: string, fileName: string) {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.click();
}

interface IBoardQrSvgProps {
  url: string;
  size?: number;
}

/** QR code do telão em SVG, com o visual padrão. */
export const BoardQrSvg = forwardRef<SVGSVGElement, IBoardQrSvgProps>(
  ({ url, size = 160 }, ref) => (
    <QRCodeSVG
      ref={ref}
      value={url}
      size={size}
      marginSize={2}
      bgColor="#ffffff"
      fgColor="#111111"
      title="QR code do telão"
    />
  ),
);
BoardQrSvg.displayName = 'BoardQrSvg';

/**
 * Downloads PNG/SVG do QR para impressão antecipada. Ligue `svgRef` num
 * `BoardQrSvg` e renderize `hiddenCanvas` em qualquer lugar do componente.
 */
export function useBoardQrDownloads(url: string, fileName: string) {
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasWrapper = useRef<HTMLDivElement>(null);

  const downloadPng = useCallback(() => {
    const canvas = canvasWrapper.current?.querySelector('canvas');
    if (canvas) download(canvas.toDataURL('image/png'), `${fileName}.png`);
  }, [fileName]);

  const downloadSvg = useCallback(() => {
    if (!svgRef.current) return;
    const xml = new XMLSerializer().serializeToString(svgRef.current);
    const blob = new Blob([xml], { type: 'image/svg+xml' });
    const href = URL.createObjectURL(blob);
    download(href, `${fileName}.svg`);
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  }, [fileName]);

  /* Canvas grande, fora da tela, só para gerar um PNG nítido. */
  const hiddenCanvas = (
    <div ref={canvasWrapper} style={{ display: 'none' }}>
      <QRCodeCanvas value={url} size={1024} marginSize={4} />
    </div>
  );

  return { svgRef, hiddenCanvas, downloadPng, downloadSvg };
}

interface IBoardQrCodeProps {
  url: string;
  size?: number;
  /** Botões de download (PNG/SVG) para impressão antecipada. */
  downloadable?: boolean;
  fileName?: string;
}

/**
 * QR code do telão, gerado no navegador — sem endpoint. Usado na tela de
 * organização (com download para imprimir) e no canto do próprio telão.
 */
const BoardQrCode: React.FC<IBoardQrCodeProps> = ({
  url,
  size = 160,
  downloadable = false,
  fileName = 'telao-qrcode',
}) => {
  const { svgRef, hiddenCanvas, downloadPng, downloadSvg } =
    useBoardQrDownloads(url, fileName);

  return (
    <div>
      <BoardQrSvg ref={svgRef} url={url} size={size} />
      {downloadable && (
        <>
          {hiddenCanvas}
          <Downloads>
            <button type="button" onClick={downloadPng}>
              <DownloadIcon
                aria-hidden
                stroke="currentColor"
                strokeWidth={1.75}
                fill="none"
              />
              Baixar PNG
            </button>
            <button type="button" onClick={downloadSvg}>
              <DownloadIcon
                aria-hidden
                stroke="currentColor"
                strokeWidth={1.75}
                fill="none"
              />
              Baixar SVG
            </button>
          </Downloads>
        </>
      )}
    </div>
  );
};

export default BoardQrCode;
